import { useCallback, useEffect, type ReactNode } from 'react';
import { Link, useLocation } from 'wouter';
import { telegram } from '../lib/telegram';
import { BackIcon, NavIcon } from './icons';
import { artistNav, isActive, isRootLocation, type NavItem } from './nav';

export { artistNav, customerNav, type NavItem } from './nav';

function BrandMark() {
  return (
    <span className="brand-lockup__mark" aria-hidden="true">
      <span className="brand-lockup__dot brand-lockup__dot--primary" />
      <span className="brand-lockup__dot brand-lockup__dot--secondary" />
    </span>
  );
}

function Navigation({
  className,
  home,
  location,
  nav,
  badges,
}: {
  className: 'side-nav' | 'bottom-nav';
  home: string;
  location: string;
  nav: NavItem[];
  badges?: Record<string, number>;
}) {
  return (
    <nav className={className} aria-label="Основная навигация">
      <Link href={home} className="bottom-nav__brand" aria-label="ArtFlow — на главную">
        <BrandMark />
        <span className="brand-lockup__name">ArtFlow</span>
      </Link>
      {nav.map((item) => {
        const active = isActive(location, item.to, nav);

        return (
          <Link
            key={item.to}
            href={item.to}
            className={`bottom-nav__item ${active ? 'is-active' : ''}`}
            aria-current={active ? 'page' : undefined}
          >
            <span className="bottom-nav__icon">
              <NavIcon name={item.icon} />
              {/* Счётчик ведёт прямо к делу: заходить в раздел «на всякий случай» не нужно. */}
              {(badges?.[item.to] ?? 0) > 0 && (
                <span className="bottom-nav__badge">{badges?.[item.to]}</span>
              )}
            </span>
            <span>{item.label}</span>
          </Link>
        );
      })}
    </nav>
  );
}

export function AppLayout({
  children,
  nav = artistNav,
  switcher,
  badges,
}: {
  children: ReactNode;
  nav?: NavItem[];
  switcher?: ReactNode;
  /** Сколько дел ждёт в разделе: ключ — адрес пункта меню. */
  badges?: Record<string, number>;
}) {
  const [location, setLocation] = useLocation();
  const home = nav[0]?.to ?? '/';
  const atRoot = isRootLocation(location, nav);

  const goBack = useCallback(() => {
    // Экран могли открыть прямой ссылкой из уведомления — тогда истории нет.
    if (window.history.length > 1) {
      window.history.back();
      return;
    }

    setLocation(home);
  }, [home, setLocation]);

  useEffect(() => {
    const back = telegram()?.BackButton;
    if (!back) return;

    if (atRoot) {
      back.hide();
      return;
    }

    back.show();
    back.onClick(goBack);

    return () => {
      back.offClick(goBack);
      back.hide();
    };
  }, [atRoot, goBack]);

  return (
    <div className="app-shell">
      <Navigation className="side-nav" home={home} location={location} nav={nav} badges={badges} />
      <div className="app-topbar">
        <div className="app-topbar__left">
          {/*
            Своя стрелка, а не только кнопка Telegram: в вебе и части клиентов
            системной кнопки нет, и с вложенного экрана некуда вернуться.
          */}
          {!atRoot && (
            <button className="icon-button icon-button--back" aria-label="Назад" onClick={goBack}>
              <BackIcon />
            </button>
          )}
          <div className="brand-lockup">
            <BrandMark />
            <span className="brand-lockup__name">ArtFlow</span>
          </div>
        </div>
        {switcher ?? <span className="plan-pill">Free</span>}
      </div>
      <main className="page">{children}</main>
      <Navigation
        className="bottom-nav"
        home={home}
        location={location}
        nav={nav}
        badges={badges}
      />
    </div>
  );
}
